import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Loader2, Check, Wand2, Crosshair, RotateCcw, Upload, Download, Image as ImageIcon, Maximize2, Sparkles, Star, Rewind } from 'lucide-react';
import { toast } from '../utils/toast';
const API = window.__API_BASE + '/api';

// Phase 3 (2026-05-19) — short labels for `sketch_versions[].source`
// shown on each filmstrip thumb so the operator instantly knows the
// provenance of every variant. Falls back to "R{n}" for legacy
// SAM-only iterations (which have no `source`).
const SOURCE_LABELS = {
  step1_initial: 'Step 1',
  step3_autocorrect: 'Step 3',
  sam_iter: 'SAM',
  text_iter: 'Text',
  force_regen: 'Regen',
  backfill: 'V0',
  keyframe_1: 'KF-1',
  enhancor_realistic_skin: 'Skin',
  enhancor_portrait_upscaler: 'Upscale',
  enhancor_general_upscaler: 'Upscale',
};

// Tailwind text colour for each source — same palette as the Stage 4
// grid card pills so the workbench and grid feel like one surface.
const SOURCE_COLORS = {
  step1_initial: '#94A3B8',     // slate-400 (neutral start state)
  step3_autocorrect: '#FBBF24', // amber-400 (auto-correction; needs attention)
  sam_iter: '#A78BFA',           // violet-400 (operator mask edit)
  text_iter: '#60A5FA',          // blue-400 (operator text edit)
  force_regen: '#34D399',        // emerald-400 (fresh re-roll)
  backfill: '#64748B',           // slate-500 (historical / pre-versioning)
  keyframe_1: '#FB7185',         // rose-400 (earlier launch frame for I2V)
  enhancor_realistic_skin: '#FBBF24',     // amber-400 (Enhancor polish)
  enhancor_portrait_upscaler: '#FBBF24',  // amber-400 (Enhancor upscale)
  enhancor_general_upscaler: '#FBBF24',   // amber-400 (Enhancor upscale)
};

/**
 * AI Photoshop Workbench — Redesigned
 * Split-view: Large canvas (left) + Controls sidebar (right)
 * Floating filmstrip for iteration history at bottom of canvas
 */
export default function SketchSamModal({ shotId, shot, onClose, onSelect, isPhotoshopMode = false, token, initialInstruction = '', engineLocked = false }) {
  const [iterations, setIterations] = useState([]);
  const [originalUrl, setOriginalUrl] = useState('');
  const [currentUrl, setCurrentUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [iterating, setIterating] = useState(false);
  const [error, setError] = useState('');

  const [feedback, setFeedback] = useState(initialInstruction);
  // Insanely Good Sketches workbench (Feb 2026): support up to 3 reference
  // images, mirroring the AI Photoshop / Keyframe workbench. The backend
  // endpoint already accepts `reference_images_b64: [b64,b64,b64]`.
  const [refImages, setRefImages] = useState([]); // [{file, preview}, ...] max 3
  const [activeRound, setActiveRound] = useState(-1);
  const [autoTriggered, setAutoTriggered] = useState(false); // -1 = original

  const [maskTool, setMaskTool] = useState('text');
  const [samPoint, setSamPoint] = useState(null);
  const [upscaling, setUpscaling] = useState(false);
  // Keyframe-1 (M-12 · 2026-05-20) — earlier launch-frame iteration.
  // Server-side pipeline (Gemini continuity → Opus 4.7 plan → gpt-image-2)
  // appends a new `keyframe_1` row to `sketch_versions[]` and flips the
  // active pointer; the workbench filmstrip then shows it like any other
  // iteration. No text input from the operator — the prompt is synthesized
  // on the server from the shot description.
  const [keyframeMinusOneRunning, setKeyframeMinusOneRunning] = useState(false);
  // Lightbox state (Feb 2026 — for the AI Photoshop / Edit Workbench
  // "click to expand" affordance requested by user). Renders a fullscreen
  // overlay showing the current canvas image at full resolution.
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const pollRef = useRef(null);
  const refFileRef = useRef(null);

  const MAX_ROUNDS = 20;

  // Per-iteration engine picker (Phase 2). For "locked" workbenches
  // (Insanely Good Sketches / Insanely Good Keyframes — see engineLocked
  // logic in IterationWorkbench) this is hidden. For AI Photoshop and the
  // standard MJ-keyframe iteration path it lets the user A/B between
  // GPT-Image-2 and Nano Banana on every Apply. Default tracks the
  // currently selected thumbnail's engine when known (option A — most
  // predictable when iterating off an earlier round). When unknown, falls
  // back to the shot's `edit_engine` or "gpt-image-2".
  const [engineChoice, setEngineChoice] = useState(() => {
    const fromShot = (shot?.edit_engine || '').toLowerCase();
    if (fromShot === 'gpt-image-2' || fromShot === 'nano-banana') return fromShot;
    return 'gpt-image-2';
  });

  // Source URL the next Apply will use. Tracks the currently displayed
  // thumbnail (R0 = original, R1+ = iteration N). Bug fix (Phase 1) —
  // previously the backend always pulled `shot.sketch_url` (latest) so
  // clicking R2 then Apply silently iterated off R5. Now we send the
  // explicit source URL the user picked, and the backend honors it.
  // `displayUrl` is an alias kept so the rest of the workbench (canvas,
  // download button, etc.) still has the same name to point at.
  const sourceUrl = activeRound >= 0 && iterations[activeRound]
    ? iterations[activeRound].image_url
    : (originalUrl || currentUrl);
  const displayUrl = sourceUrl;

  // Option A — when the user clicks a different thumbnail, default the
  // engine picker to whichever engine produced THAT round. Predictable
  // when iterating off an earlier image: "iterate this with the same
  // engine that made it" is the right baseline. User can still flip the
  // picker manually before clicking Apply.
  useEffect(() => {
    if (activeRound < 0) return;
    const r = iterations[activeRound];
    if (!r) return;
    const e = (r.engine || '').toLowerCase();
    if (e === 'gpt-image-2' || e === 'nano-banana') {
      setEngineChoice(e);
    }
  }, [activeRound, iterations]);

  // ── Data fetching ──
  //
  // Phase 3 (2026-05-19) — read from the unified `sketch_versions[]`
  // endpoint so the filmstrip shows EVERY sketch the shot has ever had:
  // Step 1 (IGS initial), Step 3 (VLM auto-correct), SAM iter, Text iter,
  // Force regen, Backfill. The legacy `/sketch-sam-iterations` endpoint
  // only ever exposed manual SAM rounds — Step 1 and Step 3 outputs were
  // invisible. We map versions to the existing `iterations` shape so the
  // rest of the modal renders unchanged.
  const fetchIterations = useCallback(async () => {
    // Guard: never fire with a missing id.
    if (!shotId) {
      setLoading(false);
      setError('No shot ID provided to Edit Workbench.');
      return;
    }
    try {
      const res = await axios.get(`${API}/shots/${shotId}/sketch/versions`);
      const versions = res.data.versions || [];
      const activeId = res.data.active_version_id;

      if (versions.length > 0) {
        // Map every version to the legacy iteration shape used by the rest
        // of the modal. New fields (source, version_id, vlm_audit) ride
        // along; old consumers ignore them.
        const mapped = versions.map(v => ({
          version_id: v.version_id,
          source: v.source,
          // Enhancor-finalized entries write `url` instead of `sketch_url`;
          // fall back so the workbench filmstrip thumb renders.
          image_url: v.sketch_url || v.url || '',
          engine: v.engine,
          feedback: v.iterate_prompt || '',
          prompt_used: v.iterate_prompt || '',
          mask_url: v.mask_url,
          overlay_url: v.overlay_url,
          point: v.point,
          vlm_audit: v.vlm_audit,
          reference_image_urls: v.reference_image_urls || [],
          created_at: v.created_at,
          is_upscaled: false,
          parent_version_id: v.parent_version_id,
        }));
        setIterations(mapped);
        setCurrentUrl(res.data.current_sketch_url || '');
        // First version's image is the closest thing to an "Original" for the
        // R0 thumb. Don't show a duplicate R0 since v[0] is already in the strip.
        setOriginalUrl('');
        // Match active to whichever version the DB flagged active.
        const activeIdx = mapped.findIndex(m => m.version_id === activeId);
        setActiveRound(activeIdx >= 0 ? activeIdx : mapped.length - 1);

        // Polling state ("is a SAM/text iter in flight right now?") still
        // lives on the legacy endpoint — fetch it best-effort.
        try {
          const old = await axios.get(`${API}/shots/${shotId}/sketch-sam-iterations`);
          setIterating(old.data.iterating || false);
          if (old.data.error) setError(old.data.error);
        } catch { /* silent */ }
      } else {
        // No versions yet — fall back to legacy endpoint. This is the path
        // for shots whose sketch was generated before Phase 1 Delivery 2
        // and never backfilled, or shots that haven't been generated yet.
        const old = await axios.get(`${API}/shots/${shotId}/sketch-sam-iterations`);
        setOriginalUrl(old.data.original_url || '');
        setCurrentUrl(old.data.current_url || '');
        setIterations(old.data.iterations || []);
        setIterating(old.data.iterating || false);
        if (old.data.error) setError(old.data.error);
        const iters = old.data.iterations || [];
        if (iters.length > 0) setActiveRound(iters.length - 1);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'unknown';
      if (status === 404) {
        setError('This shot was not found. It may have been regenerated or deleted — please refresh.');
      } else if (status === 401) {
        setError('Session expired. Please refresh the page to sign back in.');
      } else {
        setError(`Failed to load iterations (${status || 'network'}): ${detail}`);
      }
      toast.error(status === 404 ? 'Shot not found — please refresh' : 'Failed to load iterations');
    } finally {
      setLoading(false);
    }
  }, [shotId]);

  useEffect(() => {
    fetchIterations();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchIterations]);

  // Auto-trigger first edit if we have an instruction from the recipe form
  useEffect(() => {
    if (autoTriggered || !initialInstruction.trim() || !originalUrl || iterations.length > 0 || iterating || loading) return;
    setAutoTriggered(true);
    // Small delay to let UI render
    const timer = setTimeout(() => {
      handleApplyEdit();
    }, 500);
    return () => clearTimeout(timer);
  }, [originalUrl, loading]);

  // Poll while iterating
  //
  // Phase 3 — when a SAM/text/regen iteration completes, the new row is
  // pushed to BOTH the legacy `sketch_sam_iterations[]` (for the polling
  // `iterating` flag) AND `sketch_versions[]` (the unified history). We
  // poll the legacy endpoint for the cheap `iterating` boolean, and the
  // moment it flips false we re-fetch versions to pick up the new row.
  useEffect(() => {
    if (iterating) {
      pollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/shots/${shotId}/sketch-sam-iterations`);
          const still = res.data.iterating || false;
          setIterating(still);
          if (res.data.error) { setError(res.data.error); setIterating(false); }
          if (!still && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setSamPoint(null);
            // Iteration committed — pull the full unified history so the
            // filmstrip picks up the new sam_iter/text_iter row with
            // source label + VLM audit (if any).
            await fetchIterations();
          }
        } catch { /* silent */ }
      }, 4000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [iterating, shotId, fetchIterations]);

  // ── Point click handler ──
  const handlePointerDown = (e) => {
    if (iterating || maskTool !== 'point') return;
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const normX = (e.clientX - rect.left) / rect.width;
    const normY = (e.clientY - rect.top) / rect.height;
    setSamPoint({ normX, normY });
  };

  // ── Apply edit ──
  const handleApplyEdit = async () => {
    if (!feedback.trim()) return;

    // Insanely Good workbench (Feb 2026): convert each of the up-to-3 refs
    // to b64 and send as an array. We also keep `reference_image_b64`
    // (singular) populated from slot #1 for back-compat with older server
    // builds, in case the array path is ever removed.
    const refImagesB64 = [];
    for (const r of refImages) {
      if (!r?.file) continue;
      try {
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(r.file);
        });
        if (b64) refImagesB64.push(b64);
      } catch (_e) { /* skip bad file */ }
    }
    const refImageB64 = refImagesB64[0] || null;

    if (maskTool === 'text') {
      setError('');
      setIterating(true);
      try {
        await axios.post(`${API}/shots/${shotId}/sketch-sam-iterate`, {
          feedback: feedback.trim(),
          text_only: true,
          source_url: sourceUrl,
          engine: engineChoice,
          ...(refImagesB64.length > 0 && { reference_images_b64: refImagesB64 }),
          ...(refImageB64 && { reference_image_b64: refImageB64 }),
        });
        setFeedback('');
        setRefImages([]);
      } catch (err) {
        const msg = err.response?.data?.detail || 'Failed to start edit';
        setError(msg);
        setIterating(false);
        toast.error(msg);
      }
      return;
    }

    if (maskTool === 'point' && samPoint) {
      setError('');
      setIterating(true);
      try {
        await axios.post(`${API}/shots/${shotId}/sketch-sam-iterate`, {
          feedback: feedback.trim(),
          point_x: samPoint.normX,
          point_y: samPoint.normY,
          source_url: sourceUrl,
          engine: engineChoice,
          ...(refImagesB64.length > 0 && { reference_images_b64: refImagesB64 }),
          ...(refImageB64 && { reference_image_b64: refImageB64 }),
        });
        setFeedback('');
        setRefImages([]);
      } catch (err) {
        const msg = err.response?.data?.detail || 'Failed to start edit';
        setError(msg);
        setIterating(false);
        toast.error(msg);
      }
      return;
    }
  };

  const handleAccept = async (round) => {
    try {
      const target = round >= 0 ? iterations[round] : null;
      // Phase 3 — version-aware accept. Versioned rows (from
      // `sketch_versions[]`) carry a `version_id` and flip the active
      // pointer via `POST /sketch/set-active`. Legacy rows (from the
      // old `sketch_sam_iterations[]` endpoint, for shots that haven't
      // been backfilled) fall through to `select-sketch-iteration`.
      if (target?.version_id) {
        const res = await axios.post(`${API}/shots/${shotId}/sketch/set-active`, { version_id: target.version_id });
        toast.success(isPhotoshopMode ? 'Edit saved' : 'Applied to panel');
        onSelect?.(res.data.sketch_url);
      } else {
        const res = await axios.post(`${API}/shots/${shotId}/select-sketch-iteration`, { round });
        toast.success(isPhotoshopMode ? 'Edit saved' : 'Applied to panel');
        onSelect?.(res.data.sketch_url);
      }
      if (isPhotoshopMode) onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Accept failed');
    }
  };

  const handleRevert = async () => {
    try {
      const res = await axios.post(`${API}/shots/${shotId}/sketch-workbench-revert`);
      toast.success('Reverted to original');
      onSelect?.(res.data.sketch_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revert failed');
    }
  };

  // ── Keyframe-1 — earlier launch-frame iteration ──
  //
  // Server runs Gemini Vision continuity study → Opus 4.7 planner +
  // prompt synth → gpt-image-2 with the currently-active filmstrip image
  // as the reference. The new image appears as a fresh `keyframe_1` row
  // in the filmstrip; the workbench's active pointer flips to it (so the
  // canvas updates). Operator commits via the existing "Save & Close"
  // button like any other iteration. Disabled when shot.description is
  // empty (the planner needs it).
  const shotDescription = (shot?.description || '').trim();
  const canKeyframeMinusOne = !!shotDescription && !!displayUrl && !iterating && !keyframeMinusOneRunning;
  const handleKeyframeMinusOne = async () => {
    if (!canKeyframeMinusOne) return;
    const active = activeRound >= 0 ? iterations[activeRound] : null;
    const sourceVersionId = active?.version_id || null;
    // Optional operator instruction — pulled from the existing edit-instruction
    // textbox. Empty by default; when present, the planner uses it as a soft
    // guide ("prefer doors fully shut", "earliest possible start frame", etc.).
    const userInstruction = (feedback || '').trim();
    setKeyframeMinusOneRunning(true);
    setError('');
    try {
      const payload = {};
      if (sourceVersionId) payload.source_version_id = sourceVersionId;
      if (userInstruction) payload.user_instruction = userInstruction;
      await axios.post(`${API}/shots/${shotId}/sketch/keyframe-minus-one`, payload);
      toast.success('Keyframe-1 started — building launch frame…');
      // Clear the textbox so the operator doesn't accidentally re-use the
      // same instruction on a subsequent Apply Edit click.
      if (userInstruction) setFeedback('');
      // Engage the existing SAM-iteration polling loop. The backend uses
      // the same `sketch_sam_iterating` flag, so polling will pick up the
      // new keyframe_1 row the moment the background task finishes
      // (~60-180s for a Gemini → Opus → gpt-image-2 round-trip).
      setIterating(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Keyframe-1 failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setKeyframeMinusOneRunning(false);
    }
  };

  const canIterate = iterations.length < MAX_ROUNDS;
  const canSubmit = feedback.trim() && (maskTool === 'text' || (maskTool === 'point' && samPoint)) && canIterate && !iterating;

  const content = (
    <div
      className="fixed inset-0 z-[9999] flex flex-col md:flex-row overflow-y-auto md:overflow-hidden"
      style={{ background: '#050A10' }}
      data-testid="photoshop-workbench"
    >
      {/* ═══ CANVAS AREA ═══ */}
      {/* On mobile: stacks on top, ≥50vh so the image is always immediately
          visible. On desktop: grows to fill the row alongside the right panel. */}
      <div className="flex-1 relative flex flex-col overflow-hidden min-h-[50vh] md:min-h-0">
        {/* Header bar */}
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0 z-10" style={{ background: 'rgba(5,10,16,0.9)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,240,255,0.08)', border: '1px solid rgba(0,240,255,0.2)' }}>
              <Wand2 className="w-3.5 h-3.5" style={{ color: '#00F0FF' }} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white tracking-tight" data-testid="workbench-title">
                {isPhotoshopMode ? 'AI Photoshop' : 'Edit Workbench'}
              </h2>
              <p className="text-[10px] text-slate-500 font-mono tracking-wider uppercase">
                {(() => {
                  if (iterations.length === 0) return 'Original';
                  const active = iterations[activeRound];
                  if (active?.source) {
                    const label = SOURCE_LABELS[active.source] || active.source;
                    return `${label} · ${activeRound + 1} of ${iterations.length}`;
                  }
                  return `R${activeRound + 1} of ${iterations.length}`;
                })()} · {maskTool === 'point' ? 'Point Select' : 'Text Edit'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            data-testid="workbench-close"
          >
            <X className="w-5 h-5 text-slate-500 hover:text-white transition-colors" />
          </button>
        </div>

        {/* Canvas viewport — flex-column: image fills the top, filmstrip
            + VLM audit pill live in a dedicated bottom slot so they
            never overlap the sketch (2026-05-20 Rohit feedback). */}
        <div className="flex-1 relative flex flex-col overflow-hidden" ref={canvasRef}>
          {/* Dot grid background */}
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

          {/* Image area — takes remaining height above the bottom strip */}
          <div className="flex-1 relative flex items-center justify-center overflow-hidden min-h-0">
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
              <span className="text-xs text-slate-500 font-mono tracking-widest uppercase">Loading</span>
            </div>
          ) : displayUrl ? (
            <div
              className="relative max-w-full max-h-full p-4 group/canvas"
              style={{ cursor: maskTool === 'point' && !iterating ? 'crosshair' : 'default' }}
              onPointerDown={handlePointerDown}
            >
              <img
                ref={imgRef}
                src={displayUrl}
                alt="Canvas"
                className="max-w-full max-h-[40vh] md:max-h-[calc(100vh-280px)] object-contain rounded-lg select-none transition-all duration-300"
                style={{ boxShadow: '0 0 80px rgba(0,0,0,0.6)' }}
                draggable={false}
                data-testid="canvas-image"
              />

              {/* Hover toolbar — Click to expand (Feb 2026) */}
              {!iterating && (
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxUrl(displayUrl); }}
                  className="absolute top-6 right-6 p-2 rounded-lg bg-black/70 hover:bg-black/90 text-white/80 hover:text-white border border-white/15 opacity-0 group-hover/canvas:opacity-100 transition-opacity"
                  data-testid="sketch-canvas-expand-btn"
                  title="Click to expand"
                  style={{ backdropFilter: 'blur(6px)' }}
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              )}

              {/* SAM Crosshair */}
              {samPoint && maskTool === 'point' && imgRef.current && (() => {
                const rect = imgRef.current.getBoundingClientRect();
                const parentRect = canvasRef.current?.getBoundingClientRect() || rect;
                const x = rect.left - parentRect.left + samPoint.normX * rect.width;
                const y = rect.top - parentRect.top + samPoint.normY * rect.height;
                return (
                  <div
                    className="absolute pointer-events-none"
                    style={{ left: x, top: y, transform: 'translate(-50%, -50%)' }}
                    data-testid="sam-crosshair"
                  >
                    {/* Crosshair lines */}
                    <div className="absolute w-px h-8 -top-4 left-1/2 -translate-x-1/2" style={{ background: 'linear-gradient(to bottom, transparent, #00F0FF, transparent)' }} />
                    <div className="absolute h-px w-8 top-1/2 -left-4 -translate-y-1/2" style={{ background: 'linear-gradient(to right, transparent, #00F0FF, transparent)' }} />
                    {/* Center ring */}
                    <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center" style={{ borderColor: '#00F0FF', boxShadow: '0 0 12px rgba(0,240,255,0.6)' }}>
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#00F0FF' }} />
                    </div>
                  </div>
                );
              })()}

              {/* Processing overlay */}
              {iterating && (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg" style={{ background: 'rgba(5,10,16,0.7)', backdropFilter: 'blur(4px)' }}>
                  <div className="flex items-center gap-3 px-6 py-3 rounded-full" style={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(0,240,255,0.2)' }} aria-live="polite">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#00F0FF' }} />
                    <span className="text-xs font-mono tracking-widest uppercase" style={{ color: '#00F0FF' }}>Processing</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 text-center max-w-md px-6">
              <ImageIcon className="w-12 h-12 text-slate-700" />
              {error ? (
                <>
                  <p className="text-sm font-semibold text-red-300">Unable to open Edit Workbench</p>
                  <p className="text-xs text-red-200/70" data-testid="sam-modal-error">{error}</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-400">No sketch to edit yet</p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Generate a sketch for this shot first, then reopen Edit Workbench to refine it with text prompts or SAM-guided region edits.
                  </p>
                </>
              )}
            </div>
          )}
          </div>
          {/* ── Bottom strip — filmstrip (always when iterations/original exist)
                + VLM audit pill (only when active has audit). Lives in a
                dedicated flex slot BELOW the image area, never overlapping
                the sketch (2026-05-20 Rohit feedback). */}
          {(iterations.length > 0 || originalUrl || (activeRound >= 0 && iterations[activeRound]?.vlm_audit)) && (
            <div className="flex-shrink-0 flex flex-col items-center gap-2 px-3 pb-3 pt-1">
          {/* ── VLM Audit Panel (Phase 3) ──
              When the active version has a vlm_audit payload, expose
              the per-axis match flags + the correction prompt (if any)
              so the operator can see WHY a Step 3 fired or why a sketch
              passed. Hidden when no audit data is present (manual
              iterations, force-regen rows). */}
          {activeRound >= 0 && iterations[activeRound]?.vlm_audit && (() => {
            const a = iterations[activeRound].vlm_audit;
            const axes = [
              ['Cast', a.cast_match, a.cast_issues],
              ['Costume', a.costume_match, a.costume_issues],
              ['Env', a.environment_match, a.environment_issues],
              ['Comp', a.composition_match, a.composition_issues],
            ];
            const corr = iterations[activeRound].iterate_prompt;
            return (
              <div
                className="px-4 py-2 rounded-xl max-w-[640px]"
                style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)' }}
                data-testid="vlm-audit-panel"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-slate-500">VLM Audit</span>
                  <span className="text-[10px]" style={{ color: a.overall_match ? '#10B981' : '#EF4444' }}>
                    {a.overall_match ? '✓ passed' : '✗ flagged'}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {axes.map(([name, match, issues]) => (
                    <span
                      key={name}
                      className="text-[10px] inline-flex items-center gap-1"
                      style={{ color: match === false ? '#EF4444' : (match === true ? '#10B981' : '#64748B') }}
                      title={issues && typeof issues === 'string' && issues.length > 0 ? issues : (match === false ? 'flagged' : (match === true ? 'matched' : 'not evaluated'))}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'currentColor' }} />
                      {name}
                    </span>
                  ))}
                </div>
                {corr && iterations[activeRound].source === 'step3_autocorrect' && (
                  <div className="mt-1.5 pt-1.5 border-t border-white/[0.06]">
                    <span className="text-[9px] font-mono uppercase tracking-[0.1em] text-slate-500">Correction applied:</span>
                    <p className="text-[10px] text-slate-300 mt-0.5 leading-snug">{corr.slice(0, 220)}{corr.length > 220 ? '…' : ''}</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Iteration Filmstrip ── */}
          {(iterations.length > 0 || originalUrl) && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-2xl"
              style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}
              data-testid="iteration-filmstrip"
            >
              {/* Original */}
              {originalUrl && (
                <button
                  onClick={() => setActiveRound(-1)}
                  className={`relative w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 transition-all duration-200 ${activeRound === -1 ? 'scale-105' : 'opacity-60 hover:opacity-90 hover:scale-105'}`}
                  style={activeRound === -1 ? { boxShadow: '0 0 0 2px #00F0FF, 0 0 12px rgba(0,240,255,0.3)' } : { border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid="filmstrip-original"
                >
                  <img src={originalUrl} alt="Original" className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 left-0 right-0 text-[8px] font-mono text-center py-0.5" style={{ background: 'rgba(0,0,0,0.7)', color: activeRound === -1 ? '#00F0FF' : '#94A3B8' }}>R0</span>
                </button>
              )}

              {/* Iteration rounds */}
              {iterations.map((iter, i) => {
                // Phase 3 — versioned rows have a `source`; map it to a
                // short label + colour. Legacy SAM-only rows fall back
                // to "R{n}" so old shots still render sensibly.
                const labelText = iter.is_upscaled
                  ? '4K'
                  : (iter.source ? SOURCE_LABELS[iter.source] || iter.source : `R${i + 1}`);
                const labelColor = activeRound === i
                  ? '#00F0FF'
                  : (iter.source ? SOURCE_COLORS[iter.source] || '#94A3B8' : '#94A3B8');
                // VLM badge — show a small dot when this version has
                // an audit payload. Red dot = VLM flagged this version
                // (which is why a Step 3 correction was triggered);
                // green dot = VLM passed cleanly.
                const audit = iter.vlm_audit;
                const vlmPassed = audit && audit.overall_match === true;
                const vlmFailed = audit && audit.overall_match === false;
                // Enhancor badge — corner icon so operators can spot
                // skin-polish vs upscale rounds at a glance.
                const engStr = ((iter.engine) || '').toLowerCase();
                const enhancorKind = engStr.startsWith('enhancor-')
                  ? (engStr.includes('realistic_skin') ? 'skin' : 'upscale')
                  : null;
                return (
                  <button
                    key={iter.version_id || i}
                    onClick={() => setActiveRound(i)}
                    className={`relative w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 transition-all duration-200 ${activeRound === i ? 'scale-105' : 'opacity-60 hover:opacity-90 hover:scale-105'}`}
                    style={activeRound === i ? { boxShadow: '0 0 0 2px #00F0FF, 0 0 12px rgba(0,240,255,0.3)' } : { border: '1px solid rgba(255,255,255,0.1)' }}
                    data-testid={`filmstrip-r${i + 1}`}
                    title={iter.source ? `${SOURCE_LABELS[iter.source] || iter.source}${iter.iterate_prompt ? ' — ' + iter.iterate_prompt.slice(0, 80) : ''}` : ''}
                  >
                    <img src={iter.image_url} alt={labelText} className="w-full h-full object-cover" />
                    <span className="absolute bottom-0 left-0 right-0 text-[8px] font-mono text-center py-0.5" style={{ background: 'rgba(0,0,0,0.8)', color: labelColor }}>
                      {labelText}
                    </span>
                    {iter.is_upscaled && (
                      <div className="absolute top-0.5 right-0.5 px-1 py-0.5 rounded text-[6px] font-bold" style={{ background: 'rgba(168,85,247,0.8)', color: 'white' }}>4K</div>
                    )}
                    {/* Enhancor badge — sits on the right edge so it doesn't
                        clash with the VLM dot (top-left). 4K badge above
                        takes precedence on upscaled rows. */}
                    {enhancorKind && !iter.is_upscaled && (
                      <span
                        className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(245,158,11,0.92)', boxShadow: '0 0 0 1px rgba(0,0,0,0.5)' }}
                        data-testid={`filmstrip-r${i + 1}-enhancor-badge`}
                        title={enhancorKind === 'skin' ? 'Skin enhanced via Enhancor' : 'Upscaled via Enhancor'}
                      >
                        {enhancorKind === 'skin'
                          ? <Sparkles className="w-2 h-2 text-black" strokeWidth={3} />
                          : <Wand2 className="w-2 h-2 text-black" strokeWidth={3} />}
                      </span>
                    )}
                    {/* VLM badge dot */}
                    {(vlmPassed || vlmFailed) && (
                      <div
                        className="absolute top-0.5 left-0.5 w-2 h-2 rounded-full"
                        style={{ background: vlmFailed ? '#EF4444' : '#10B981', boxShadow: '0 0 4px rgba(0,0,0,0.6)' }}
                        title={vlmFailed ? 'VLM flagged this version' : 'VLM passed'}
                      />
                    )}
                  </button>
                );
              })}

              {/* Download active */}
              {displayUrl && (
                <a
                  href={displayUrl}
                  download={`edit_R${activeRound + 1}.png`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-white/10 ml-1"
                  style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid="filmstrip-download"
                >
                  <Download className="w-3.5 h-3.5 text-slate-400" />
                </a>
              )}
            </div>
          )}
            </div>
          )}
        </div>
      </div>

      {/* ═══ SIDEBAR ═══ */}
      <div
        className="w-full md:w-[360px] lg:w-[380px] flex-shrink-0 flex flex-col border-t md:border-t-0 md:border-l overflow-y-auto"
        style={{ background: '#0A1520', borderColor: 'rgba(255,255,255,0.06)' }}
        data-testid="workbench-sidebar"
      >
        {/* Error */}
        {error && (
          <div className="mx-5 mt-4 p-3 rounded-lg text-xs border" style={{ color: '#FF3B30', background: 'rgba(255,59,48,0.06)', borderColor: 'rgba(255,59,48,0.15)' }}>
            {error}
          </div>
        )}

        <div className="flex-1 flex flex-col p-5 gap-5">
          {/* Mode Selector */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-2">Mode</label>
            <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.05)' }} data-testid="mode-selector">
              <button
                onClick={() => { setMaskTool('text'); setSamPoint(null); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-xs font-semibold transition-all ${maskTool === 'text' ? 'bg-white/10 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                data-testid="tab-text-edit"
              >
                <Wand2 className="w-3.5 h-3.5" /> Text Edit
              </button>
              <button
                onClick={() => { setMaskTool('point'); setSamPoint(null); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-xs font-semibold transition-all ${maskTool === 'point' ? 'bg-white/10 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                data-testid="tab-point-select"
              >
                <Crosshair className="w-3.5 h-3.5" /> Point Select
              </button>
            </div>
            <p className="text-[10px] text-slate-600 mt-2 leading-relaxed">
              {maskTool === 'text'
                ? 'Describe the edit — AI applies it to the full image.'
                : 'Click a subject on the canvas, then describe the change.'}
            </p>
          </div>

          {/* Point status */}
          {maskTool === 'point' && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: samPoint ? 'rgba(0,240,255,0.05)' : 'rgba(255,255,255,0.02)', border: `1px solid ${samPoint ? 'rgba(0,240,255,0.15)' : 'rgba(255,255,255,0.05)'}` }}>
              <div className={`w-2 h-2 rounded-full ${samPoint ? 'animate-pulse' : ''}`} style={{ background: samPoint ? '#00F0FF' : '#475569' }} />
              <span className="text-[10px] font-mono" style={{ color: samPoint ? '#00F0FF' : '#475569' }}>
                {samPoint ? `Point: ${(samPoint.normX * 100).toFixed(0)}%, ${(samPoint.normY * 100).toFixed(0)}%` : 'Click on canvas to select'}
              </span>
              {samPoint && (
                <button onClick={() => setSamPoint(null)} className="ml-auto p-1 rounded hover:bg-white/5" data-testid="clear-sam-point">
                  <X className="w-3 h-3 text-slate-500" />
                </button>
              )}
            </div>
          )}

          {/* Edit Instruction */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-2">Edit Instruction</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={maskTool === 'text' ? "e.g. 'Change the sky to golden hour sunset'" : "e.g. 'Replace with red sneakers'"}
              className="w-full h-24 text-sm px-4 py-3 text-white rounded-xl outline-none resize-none transition-all"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              onFocus={(e) => e.target.style.borderColor = 'rgba(0,240,255,0.3)'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
                  e.preventDefault();
                  handleApplyEdit();
                }
              }}
              disabled={iterating}
              data-testid="edit-instruction-input"
            />
          </div>

          {/* Reference Images — up to 3 slots, mirroring the AI Photoshop /
              Keyframe workbench. Insanely Good Sketches benefits from
              identity (cast), wardrobe (costume) and environment (location)
              refs in addition to the user's spec sheet, so we expose 3
              slots and send them as an array. */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-2">Reference images (optional · up to 3)</label>
            <input type="file" ref={refFileRef} accept="image/*" className="hidden" onChange={(e) => {
              const file = e.target.files[0];
              if (!file) return;
              if (refImages.length >= 3) {
                toast.error('Up to 3 reference images');
                if (refFileRef.current) refFileRef.current.value = '';
                return;
              }
              const reader = new FileReader();
              reader.onload = (ev) => setRefImages((prev) => [...prev, { file, preview: ev.target.result }]);
              reader.readAsDataURL(file);
              if (refFileRef.current) refFileRef.current.value = '';
            }} />
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map((idx) => {
                const r = refImages[idx];
                const isNextSlot = refImages.length === idx;
                if (r) {
                  return (
                    <div
                      key={idx}
                      className="relative aspect-square rounded-xl overflow-hidden group"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                      data-testid={`sketch-ref-slot-${idx}`}
                    >
                      <img src={r.preview} alt={`ref ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => setRefImages((prev) => prev.filter((_, i) => i !== idx))}
                        className="absolute top-1 right-1 p-1 rounded-md bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                        data-testid={`sketch-ref-remove-${idx}`}
                        title="Remove reference"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                      <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold tracking-wider"
                        style={{ background: 'rgba(0,0,0,0.55)', color: '#94A3B8' }}
                      >
                        REF {idx + 1}
                      </div>
                    </div>
                  );
                }
                return (
                  <button
                    key={idx}
                    onClick={() => isNextSlot && refFileRef.current?.click()}
                    disabled={!isNextSlot || iterating}
                    className={`aspect-square rounded-xl flex flex-col items-center justify-center gap-1 transition-all ${isNextSlot ? 'text-slate-500 hover:text-slate-300 cursor-pointer' : 'text-slate-700 cursor-not-allowed opacity-50'}`}
                    style={{ border: '1px dashed rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.01)' }}
                    data-testid={`sketch-ref-add-${idx}`}
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span className="text-[9px] uppercase tracking-wider">Ref {idx + 1}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Engine Picker — visible by default; hidden when the parent
              locks the engine (Insanely Good Sketches / Keyframes pin to
              GPT-Image-2). Click-to-toggle on every iteration — option A:
              defaults to the engine of the currently selected thumbnail. */}
          {!engineLocked && (
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-2">Edit Engine</label>
              <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.05)' }} data-testid="engine-picker">
                <button
                  onClick={() => setEngineChoice('gpt-image-2')}
                  disabled={iterating}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-semibold transition-all ${engineChoice === 'gpt-image-2' ? 'bg-emerald-500/20 text-emerald-300 shadow-sm ring-1 ring-emerald-500/40' : 'text-slate-400 hover:text-white'}`}
                  data-testid="engine-pick-gpt-image-2"
                  title="OpenAI GPT-Image-2 — sharper photo edits, slower (~25s), better at preserving fine detail."
                >
                  <Sparkles className="w-3 h-3" /> GPT-Image-2
                </button>
                <button
                  onClick={() => setEngineChoice('nano-banana')}
                  disabled={iterating}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-semibold transition-all ${engineChoice === 'nano-banana' ? 'bg-purple-500/20 text-purple-300 shadow-sm ring-1 ring-purple-500/40' : 'text-slate-400 hover:text-white'}`}
                  data-testid="engine-pick-nano-banana"
                  title="Gemini 'Nano Banana' — faster (~8s), more creative variance, better for abstract/stylised edits."
                >
                  <Wand2 className="w-3 h-3" /> Nano Banana
                </button>
              </div>
              <p className="text-[10px] text-slate-600 mt-2 leading-relaxed">
                {engineChoice === 'gpt-image-2'
                  ? 'Sharper, slower (~25s). Best for realism + fine-detail edits.'
                  : 'Faster (~8s), more creative variance. Best for abstract / stylised edits.'}
              </p>
            </div>
          )}

          {/* Apply Button */}
          <button
            onClick={handleApplyEdit}
            disabled={!canSubmit}
            className="w-full py-4 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-20"
            style={{
              background: iterating ? 'rgba(0,240,255,0.08)' : canSubmit ? '#00F0FF' : 'rgba(0,240,255,0.08)',
              color: iterating || !canSubmit ? '#00F0FF' : '#050A10',
              border: iterating ? '1px solid rgba(0,240,255,0.2)' : 'none',
            }}
            data-testid="apply-edit-button"
          >
            {iterating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
            ) : (
              <><Wand2 className="w-4 h-4" /> Apply Edit</>
            )}
          </button>

          {!canIterate && iterations.length >= MAX_ROUNDS && (
            <p className="text-[10px] text-amber-400/60 text-center font-mono">Max {MAX_ROUNDS} rounds reached</p>
          )}

          {/* Keyframe-1 — earlier launch-frame iteration (M-12) */}
          <div>
            <button
              onClick={handleKeyframeMinusOne}
              disabled={!canKeyframeMinusOne}
              className="w-full py-3 rounded-xl text-[11px] font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-30"
              style={{ background: 'rgba(251,113,133,0.08)', color: '#FB7185', border: '1px solid rgba(251,113,133,0.25)' }}
              data-testid="workbench-keyframe-minus-one-btn"
              title={
                !shotDescription
                  ? 'Keyframe-1 needs a shot description to plan the launch frame.'
                  : 'Generate an EARLIER launch frame from the active image. Uses the shot description automatically; any text in the box above is treated as an optional hint to the planner. ~30-60s.'
              }
            >
              {keyframeMinusOneRunning ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating launch frame…</>
              ) : (
                <><Rewind className="w-3.5 h-3.5" /> Keyframe-1 (earlier launch frame)</>
              )}
            </button>
            <p className="text-[10px] text-slate-600 mt-1.5 leading-relaxed">
              {shotDescription
                ? 'Generates the earliest plausible launch frame of this shot’s motion arc — ideal as an I2V starting frame. Type an optional hint above (e.g. "doors fully shut") to steer the planner, or leave it blank to let it pick.'
                : 'Disabled — this shot has no description, which Keyframe-1 needs to plan the launch frame.'}
            </p>
          </div>
        </div>

        {/* Accept / Revert */}
        {iterations.length > 0 && (
          <div className="p-5 space-y-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            <button
              onClick={() => handleAccept(activeRound >= 0 ? activeRound : iterations.length - 1)}
              className="w-full py-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2"
              style={{ background: 'rgba(0,240,255,0.1)', color: '#00F0FF', border: '1px solid rgba(0,240,255,0.2)' }}
              data-testid="accept-edit-button"
            >
              <Check className="w-3.5 h-3.5" />
              {isPhotoshopMode ? 'Save & Close' : `Accept R${(activeRound >= 0 ? activeRound : iterations.length - 1) + 1}`}
            </button>
            {/* Add to Gallery — AI Photoshop only. Adds the currently-displayed
                iteration to the user's persistent gallery (cross-run history)
                WITHOUT closing the workbench, so the user can keep iterating
                and add multiple frames to the gallery in the same session. */}
            {isPhotoshopMode && (
              <button
                onClick={async () => {
                  if (!displayUrl) return;
                  try {
                    const r = iterations[activeRound];
                    const res = await axios.post(`${API}/ai-photoshop/gallery`, {
                      image_url: displayUrl,
                      edit_instruction: r?.feedback || feedback || '',
                      source_url: r?.source_url || originalUrl,
                      original_url: originalUrl,
                      engine: r?.engine || engineChoice,
                    });
                    toast.success(res.data?.deduped ? 'Already in Gallery' : 'Added to Gallery ★');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Could not add to Gallery');
                  }
                }}
                disabled={!displayUrl}
                className="w-full py-2.5 rounded-xl text-[11px] font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-30"
                style={{ background: 'rgba(250,204,21,0.08)', color: '#FACC15', border: '1px solid rgba(250,204,21,0.2)' }}
                data-testid="add-to-gallery-button"
                title="Add this iteration to your AI Photoshop Gallery. The workbench stays open so you can keep iterating."
              >
                <Star className="w-3.5 h-3.5" /> Add to Gallery
              </button>
            )}
            {/* Topaz Upscale Button */}
            <button
              onClick={async () => {
                const imgUrl = displayUrl;
                if (!imgUrl) return;
                setUpscaling(true);
                try {
                  const resp = await axios.post(`${API}/recipes/vlm-qc/topaz-upscale`, {
                    image_url: imgUrl,
                    upscale_factor: 4,
                    model: 'Standard V2',
                  });
                  const upscaledUrl = resp.data.url;
                  // Add as a new iteration with "Upscaled" badge
                  setIterations(prev => [...prev, { image_url: upscaledUrl, feedback: '4K Upscale (Topaz)', is_upscaled: true }]);
                  setActiveRound(iterations.length);
                  toast.success('Upscaled to 4K');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Upscale failed');
                } finally {
                  setUpscaling(false);
                }
              }}
              disabled={upscaling || !displayUrl}
              className="w-full py-2.5 rounded-xl text-[11px] font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-30"
              style={{ background: 'rgba(168,85,247,0.08)', color: '#A78BFA', border: '1px solid rgba(168,85,247,0.2)' }}
              data-testid="topaz-upscale-button"
            >
              {upscaling ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Upscaling to 4K...</>
              ) : (
                <><Maximize2 className="w-3.5 h-3.5" /> Upscale to 4K (Topaz)</>
              )}
            </button>
            <button
              onClick={handleRevert}
              className="w-full py-2.5 rounded-xl text-[11px] font-medium transition-all flex items-center justify-center gap-2 hover:bg-red-500/5"
              style={{ color: '#FF3B30', border: '1px solid rgba(255,59,48,0.15)' }}
              data-testid="revert-button"
            >
              <RotateCcw className="w-3 h-3" /> Revert to Original
            </button>
          </div>
        )}
      </div>
      {/* Lightbox overlay — Feb 2026 (click-to-expand on canvas) */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
          style={{ background: 'rgba(0,0,0,0.92)' }}
          onClick={() => setLightboxUrl(null)}
          data-testid="sketch-lightbox-overlay"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20"
            data-testid="sketch-lightbox-close"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
          <a
            href={lightboxUrl}
            download={`edit_R${activeRound + 1}.png`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute top-4 right-16 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 inline-flex items-center justify-center"
            data-testid="sketch-lightbox-download"
            title="Download image"
          >
            <Download className="w-5 h-5" />
          </a>
          <img
            src={lightboxUrl}
            alt="Expanded view"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );

  return typeof document !== 'undefined' ? ReactDOM.createPortal(content, document.body) : null;
}
